import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { useCallback } from 'react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';

import { ReadingsEventKind } from '@crossnokaye/typescript-sdk/models/components';

import { CompactReadingEvent, compactReadingEventFromJSON } from '@repo/sdk/models/components';

import { useCurrentFacility } from '@/hooks/use-current-facility';
import { useStream } from '@/hooks/use-stream';
import { jotaiStore } from '@/providers/jotai/jotai-store';
import { readingsStreamQuery } from '@/queries/gaia-api/readings-stream-query';
import { STREAM_TYPES } from '@/state/stream-status.state';
import {
  convertCompactReadingEventToReadingEvents,
  LatestValue,
  ReadingEvent,
} from '@/types/reading-types';

export type SubscriptionEvent = {
  point_ids: string[];
  kill?: boolean;
};

// build a readings family atom so that we can use it to pull out readings by pointId
export const readingsFamily = atomFamily(
  (reading: LatestValue) => atom(reading),
  (a: LatestValue, b: LatestValue) => a.pointId === b.pointId
);

/* this hook is responsible for opens the websockets and managing the streamed events */
export const useReadingsStream = () => {
  const { agentId } = useCurrentFacility();

  const queryClient: QueryClient = useQueryClient();
  const { webSocketUrl, enabled, queryKey } = readingsStreamQuery({ agentId });

  const handleReadingEvent = useCallback(
    (event: ReadingEvent) => {
      switch (event.type) {
        case ReadingsEventKind.Keepalive: {
          // Keep Alive is a keep alive message.
          // Maybe: A single keepalive cache for all sockets?
          console.debug(`Readings keep alive at ${new Date().toISOString()}`);
          break;
        }
        case ReadingsEventKind.Reading: {
          if (event.latestValue) {
            jotaiStore.set(readingsFamily(event.latestValue), event.latestValue);
          }

          // Update the latest value for the point.
          const pointQueryKey = queryKey.concat(event.latestValue?.pointId);

          // Find all queries that start with this pointQueryKey pattern
          const matchingQueries = queryClient.getQueryCache().findAll({
            predicate: (query) => {
              const key = query.queryKey as string[];
              // Check if the query key starts with our pointQueryKey
              return (
                key.length >= pointQueryKey.length &&
                pointQueryKey.every((segment, index) => segment === key[index])
              );
            },
          });

          // Update all matching queries
          matchingQueries.forEach((query) => {
            queryClient.setQueryData(query.queryKey, (old: any) => ({
              ...old,
              latestValue: event.latestValue,
              subscribed: true,
            }));
          });
          break;
        }
        default:
          break;
      }
    },
    [queryClient, queryKey]
  );
  return useStream<CompactReadingEvent, SubscriptionEvent>(enabled ? webSocketUrl : null, {
    onReceiveContent: (compactEvent: CompactReadingEvent) => {
      // Convert compact event to individual reading events
      const readingEvents = convertCompactReadingEventToReadingEvents(compactEvent);

      // Process each individual reading event
      readingEvents.forEach((readingEvent) => {
        handleReadingEvent(readingEvent);
      });
    },
    parse: compactReadingEventFromJSON,
    streamType: STREAM_TYPES.READINGS,
    displayName: 'Readings',
  });
};
